import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Switch,
  Button,
  IconButton,
  CircularProgress,
  Chip,
  Tooltip
} from '@mui/material';
import { Settings, Add, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFetchMarketplaces } from '../../../marketplace/hooks/useFetchMarketplaces';
import { useMarketplaces } from '../../../marketplace/hooks/useMarketplaces';
import { useWhatsApp } from '../../../marketplace/hooks/useWhatsApp';
import { useIFood } from '../../../marketplace/hooks/useIFood';
import { useCaixa } from '../../../financeiro/hooks/useCaixa';
import { useFetchCaixaAberto } from '../../../financeiro/hooks/useFetchCaixaAberto';
import { IMarketplace } from '../../../marketplace/interfaces/IMarketplace';
import { IStatusEstabelecimentoIFood } from '../../../marketplace/interfaces/IStatusEstabelecimentoIFood';
import { TipoMarketplace, TipoMarketplaceTitulo } from '../../../marketplace/enum/TipoMarketplace';
import { ModalAbrirCaixa } from '../AbrirCaixaModal';
import { WhatsAppQRCodeModal } from '../WhatsAppQRCodeModal';
import { useThemeContext } from '../../contexts/ThemeContext';

export const MarketplacesDashboardCard: React.FC = () => {
  const navigate = useNavigate();
  const { themeName } = useThemeContext();
  const isDark = themeName === 'dark';

  // Marketplace hooks
  const { marketplaces, loading: loadingMarketplaces, handleRefresh } = useFetchMarketplaces();
  const { salvar } = useMarketplaces();
  const { consultarStatus, desconectar } = useWhatsApp();
  const { consultarStatus: consultarStatusIfood } = useIFood();

  // Cash register hooks
  const { abrirCaixa, fecharCaixa, loading: loadingCaixa } = useCaixa();
  const { caixa, ultimoCaixa, handleRefresh: refreshCaixa } = useFetchCaixaAberto();

  // Modal states
  const [abrirCaixaModalOpen, setAbrirCaixaModalOpen] = useState(false);
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [selectedWhatsAppMarketplace, setSelectedWhatsAppMarketplace] = useState<string>('');

  // WhatsApp connection states
  const [whatsappStatuses, setWhatsappStatuses] = useState<Record<string, boolean>>({});

  // iFood statuses (read from iFood API — not editable by the user)
  const [ifoodStatuses, setIfoodStatuses] = useState<Record<string, IStatusEstabelecimentoIFood[]>>({});

  // Optimistic state for marketplaces (for smooth UI updates)
  const [optimisticStates, setOptimisticStates] = useState<Record<string, boolean>>({});

  // Check WhatsApp connection status for all WhatsApp marketplaces
  useEffect(() => {
    if (!marketplaces) return;

    const checkWhatsAppStatuses = async () => {
      const whatsappMarketplaces = marketplaces.filter(m => m.tipo === TipoMarketplace.WHATSAPP);

      for (const marketplace of whatsappMarketplaces) {
        if (marketplace.uuid) {
          const status = await consultarStatus(marketplace.uuid);
          setWhatsappStatuses(prev => ({
            ...prev,
            [marketplace.uuid!]: status ?? false
          }));
        }
      }
    };

    checkWhatsAppStatuses();
    const interval = setInterval(checkWhatsAppStatuses, 10000);

    return () => clearInterval(interval);
  }, [marketplaces]);

  // Fetch iFood status from the iFood API (read-only — managed by iFood)
  useEffect(() => {
    if (!marketplaces) return;
    const ifoodMarketplaces = marketplaces.filter(m => m.tipo === TipoMarketplace.IFOOD);
    if (ifoodMarketplaces.length === 0) return;

    const fetchIfoodStatuses = async () => {
      for (const marketplace of ifoodMarketplaces) {
        if (!marketplace.uuid) continue;
        const status = await consultarStatusIfood(marketplace.uuid);
        if (status && status.length > 0) {
          setIfoodStatuses(prev => ({
            ...prev,
            [marketplace.uuid!]: status
          }));
        }
      }
    };

    fetchIfoodStatuses();
  }, [marketplaces]);

  const handlePDVToggle = async () => {
    if (loadingCaixa) return;

    if (caixa) {
      await fecharCaixa();
      refreshCaixa();
    } else {
      setAbrirCaixaModalOpen(true);
    }
  };

  const handleAbrirCaixa = async (descricao: string, valorInicial: number): Promise<boolean | null> => {
    const resultado = await abrirCaixa(descricao, valorInicial);
    if (resultado) {
      setAbrirCaixaModalOpen(false);
      refreshCaixa();
    }
    return resultado;
  };

  const handleMarketplaceToggle = async (marketplace: IMarketplace, isOpen: boolean) => {
    if (!marketplace.uuid) return;

    // Special handling for WhatsApp
    if (marketplace.tipo === TipoMarketplace.WHATSAPP) {
      const isConnected = whatsappStatuses[marketplace.uuid];

      if (!isConnected && isOpen) {
        setSelectedWhatsAppMarketplace(marketplace.uuid);
        setWhatsappModalOpen(true);
        return;
      }

      if (isConnected && !isOpen && marketplace.uuid) {
        setWhatsappStatuses(prev => ({
          ...prev,
          [marketplace.uuid!]: false
        }));

        const desconectado = await desconectar(marketplace.uuid);
        if (!desconectado) {
          const statusAtual = await consultarStatus(marketplace.uuid);
          setWhatsappStatuses(prev => ({
            ...prev,
            [marketplace.uuid!]: statusAtual ?? false
          }));
          setOptimisticStates(prev => {
            const newState = { ...prev };
            delete newState[marketplace.uuid!];
            return newState;
          });
          return;
        }
      }
    }

    // Optimistic update
    setOptimisticStates(prev => ({
      ...prev,
      [marketplace.uuid!]: isOpen
    }));

    try {
      await salvar({
        ...marketplace,
        configuracao: {
          ...marketplace.configuracao,
          status: {
            aberto: isOpen,
            mensagem: marketplace.configuracao?.status?.mensagem,
            dataHoraUltimaAtualizacao: new Date()
          }
        }
      });

      handleRefresh();

      if (marketplace.tipo === TipoMarketplace.WHATSAPP && marketplace.uuid) {
        setTimeout(async () => {
          const statusAtual = await consultarStatus(marketplace.uuid!);
          setWhatsappStatuses(prev => ({
            ...prev,
            [marketplace.uuid!]: statusAtual ?? false
          }));
          setOptimisticStates(prev => {
            const newState = { ...prev };
            delete newState[marketplace.uuid!];
            return newState;
          });
        }, 500);
      } else {
        setOptimisticStates(prev => {
          const newState = { ...prev };
          delete newState[marketplace.uuid!];
          return newState;
        });
      }
    } catch (error) {
      console.error('Erro ao alterar status do marketplace:', error);
      setOptimisticStates(prev => {
        const newState = { ...prev };
        delete newState[marketplace.uuid!];
        return newState;
      });
      if (marketplace.tipo === TipoMarketplace.WHATSAPP && marketplace.uuid) {
        const statusAtual = await consultarStatus(marketplace.uuid);
        setWhatsappStatuses(prev => ({
          ...prev,
          [marketplace.uuid!]: statusAtual ?? false
        }));
      }
    }
  };

  const handleWhatsAppModalClose = () => {
    setWhatsappModalOpen(false);
    setSelectedWhatsAppMarketplace('');
    handleRefresh();
  };

  // Filter and sort marketplaces
  const relevantMarketplaces = marketplaces?.filter(m =>
    m.tipo === TipoMarketplace.IFOOD ||
    m.tipo === TipoMarketplace.WHATSAPP ||
    m.tipo === TipoMarketplace.CARDAPIO_DIGITAL_DELIVERY_RETIRADA
  ) || [];

  if (loadingMarketplaces) {
    return (
      <Card sx={{
        width: "100%",
        height: "500px",
        padding: "1.5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <CircularProgress />
      </Card>
    );
  }

  return (
    <>
      <Card sx={{
        width: "100%",
        height: "500px",
        padding: "1.5rem",
        display: "flex",
        flexDirection: "column",
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          transform: 'translateY(-2px)',
        }
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Marketplaces
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Status de integração
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => navigate('/marketplace')}
            sx={{
              '&:hover': {
                bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <Settings fontSize="small" sx={{ color: 'text.secondary' }} />
          </IconButton>
        </Box>

        {/* Marketplaces List */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, overflowY: 'auto' }}>
          {/* PDV - Always first */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
            borderRadius: '12px',
            border: 1,
            borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" fontWeight="600">
                PDV
              </Typography>
              <Chip
                label={caixa ? "Canal Aberto" : "Canal Fechado"}
                size="small"
                sx={{
                  height: '18px',
                  fontSize: '10px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mt: 0.5,
                  bgcolor: caixa
                    ? 'rgba(16, 185, 129, 0.1)'
                    : 'rgba(107, 114, 128, 0.1)',
                  color: caixa
                    ? 'rgb(16, 185, 129)'
                    : 'rgb(107, 114, 128)',
                  border: 'none',
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={!!caixa}
                onChange={handlePDVToggle}
                disabled={loadingCaixa}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'rgb(16, 185, 129)',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'rgb(16, 185, 129)',
                  },
                }}
              />
            </Box>
          </Box>

          {/* Other Marketplaces */}
          {relevantMarketplaces.map(marketplace => {
            const isIfood = marketplace.tipo === TipoMarketplace.IFOOD;
            const ifoodStatusData = isIfood ? (ifoodStatuses[marketplace.uuid!] ?? []) : [];

            let realState: boolean;
            if (marketplace.tipo === TipoMarketplace.WHATSAPP) {
              realState = whatsappStatuses[marketplace.uuid!] ?? false;
            } else if (isIfood) {
              realState = ifoodStatusData.length > 0 ? ifoodStatusData.every(s => s.available) : false;
            } else {
              realState = marketplace.configuracao?.status?.aberto ?? false;
            }

            // iFood não usa optimistic state pois o switch é somente leitura
            const optimisticState = isIfood ? undefined : optimisticStates[marketplace.uuid!];
            const isOpen = optimisticState !== undefined ? optimisticState : realState;

            const ifoodTooltip = ifoodStatusData.length > 0
              ? Array.from(new Set(ifoodStatusData.map(s => s.message.title))).join(' · ')
              : 'Status gerenciado pelo iFood';

            return (
              <Box key={marketplace.uuid} sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                borderRadius: '12px',
                border: 1,
                borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
              }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" fontWeight="600">
                    {TipoMarketplaceTitulo[marketplace.tipo as keyof typeof TipoMarketplaceTitulo] || marketplace.tipo}
                  </Typography>
                  <Chip
                    label={isOpen ? "Canal Aberto" : "Canal Fechado"}
                    size="small"
                    sx={{
                      height: '18px',
                      fontSize: '10px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      mt: 0.5,
                      bgcolor: isOpen
                        ? 'rgba(16, 185, 129, 0.1)'
                        : 'rgba(107, 114, 128, 0.1)',
                      color: isOpen
                        ? 'rgb(16, 185, 129)'
                        : 'rgb(107, 114, 128)',
                      border: 'none',
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {isIfood ? (
                    <Tooltip title={ifoodTooltip}>
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        <Switch
                          checked={isOpen}
                          disabled
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: 'rgb(16, 185, 129)',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: 'rgb(16, 185, 129)',
                            },
                          }}
                        />
                        <Lock fontSize="small" sx={{ color: 'text.disabled', mr: 0.5 }} />
                      </span>
                    </Tooltip>
                  ) : (
                    <Switch
                      checked={isOpen}
                      onChange={(e) => handleMarketplaceToggle(marketplace, e.target.checked)}
                      sx={{
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '& .MuiSwitch-switchBase': {
                          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        },
                        '& .MuiSwitch-track': {
                          transition: 'background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'rgb(16, 185, 129)',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'rgb(16, 185, 129)',
                        },
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Add Channel Button */}
        <Button
          fullWidth
          onClick={() => navigate('/marketplace/cadastrar')}
          sx={{
            mt: 2,
            py: 1,
            fontSize: '12px',
            fontWeight: 600,
            color: 'text.secondary',
            borderStyle: 'dashed',
            borderWidth: 1,
            borderColor: isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
            '&:hover': {
              bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              borderColor: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
            }
          }}
          startIcon={<Add fontSize="small" />}
        >
          Configurar Canal
        </Button>
      </Card>

      {/* Modals */}
      <ModalAbrirCaixa
        formularioAberto={abrirCaixaModalOpen}
        fecharFormulario={() => setAbrirCaixaModalOpen(false)}
        abrirCaixa={handleAbrirCaixa}
        ultimoCaixa={ultimoCaixa}
      />

      <WhatsAppQRCodeModal
        open={whatsappModalOpen}
        onClose={handleWhatsAppModalClose}
        marketplaceUuid={selectedWhatsAppMarketplace}
      />
    </>
  );
};
